import { isNumber } from 'lodash';
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { DraggableCore, DraggableEventHandler } from 'react-draggable';
import { isPad } from '@/utils/browser-kit';

const HIERARCHY_PANEL_LEAVE_ANIMATION_NAME = 'hierarchyPanelZoomBigOut';
type DragPopoverPosition = Pick<React.CSSProperties, 'top' | 'right' | 'bottom' | 'left'>;

// 解析CSS位置值的辅助函数
const parsePositionValue = (value: string | number | undefined) => {
    if (value === undefined || value === null) return 0;
    if (isNumber(value)) return value;
    if (typeof value === 'string') {
        if (value === 'auto') return 0;
        const parsed = parseFloat(value);
        return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
};

const DragPopover: React.FC<{
    children?: React.ReactNode;
    handleClassName?: string;
    cancelClassName?: string;
    initialPosition?: DragPopoverPosition;
    motion?: 'enter' | 'leave';
    onMotionEnd?: () => void;
}> = React.memo(({ handleClassName, cancelClassName, children, initialPosition, motion, onMotionEnd }) => {
    const [position, setPosition] = useState(initialPosition);

    const onDrag: DraggableEventHandler = (_e, data) => {
        setPosition((p) => {
            // 如果没有任何定位属性，默认使用值
            if (!p || (!p.top && !p.right && !p.bottom && !p.left)) {
                return {
                    left: 0,
                    top: 0,
                };
            }

            // 只计算和返回已存在的定位属性
            const result: DragPopoverPosition = {};

            if (p.top !== undefined) {
                result.top = parsePositionValue(p.top) + data.deltaY;
            }
            if (p.right !== undefined) {
                result.right = parsePositionValue(p.right) - data.deltaX;
            }
            if (p.bottom !== undefined) {
                result.bottom = parsePositionValue(p.bottom) - data.deltaY;
            }
            if (p.left !== undefined) {
                result.left = parsePositionValue(p.left) + data.deltaX;
            }

            return result;
        });
    };

    const motionClassName = motion ? `hierarchy-panel-motion hierarchy-panel-motion--${motion}` : undefined;
    const host = (
        <div
            className={motionClassName}
            style={{
                position: 'absolute',
                zIndex: 12,
                ...position,
            }}
            onAnimationEnd={(event) => {
                // 只接受当前宿主自己的退出动画；子节点、进入动画和已取消的旧动画都不能触发卸载。
                if (
                    event.target !== event.currentTarget ||
                    motion !== 'leave' ||
                    event.animationName !== HIERARCHY_PANEL_LEAVE_ANIMATION_NAME
                ) {
                    return;
                }
                onMotionEnd?.();
            }}
        >
            {children}
        </div>
    );

    return createPortal(
        // iPad 延续固定面板交互；桌面端独立 Panel 才包装 DraggableCore。
        isPad ? (
            host
        ) : (
            // DraggableCore 监听这个宿主的冒泡事件，Panel 根节点不能拦截 mousedown。
            <DraggableCore onDrag={onDrag} handle={`.${handleClassName}`} cancel={`.${cancelClassName}`}>
                {host}
            </DraggableCore>
        ),
        document.body,
    );
});

export default DragPopover;
